import type { DirectoryEntityType } from '@adminhub/graphql-types';

import type { FeatureEvents, ScreenEvent, TrackEvent, UIEvent } from '../new-analytics-types';
import { Action, ActionSubject, TrackAction, TrackActionSubject } from '../new-analytics-types';

type ScreenKeys =
  | 'agentsOverviewScreen'
  | 'agentsAccessScreen'
  | 'agentsDefaultAppAccessModalScreen'
  | 'agentsDefaultGroupAccessModalScreen'
  | 'agentProfileScreen'
  | 'updateAccessDrawer'
  | 'addGroupsToAgentModalScreen';

type UIEventKeys = 'addGroupsToAllAgentsClicked' | 'grantAccessClicked' | 'addGroupsToAgentModalClicked';

type TrackEventKeys = 'addedAppToAgent' | 'removeAppToAgent' | 'addedGroupToAgent' | 'removeGroupToAgent';

export enum AgentsScreens {
  agentsOverviewScreen = 'agentsOverviewScreen',
  agentsAccessScreen = 'agentsAccessScreen',
  agentsDefaultAppAccessModalScreen = 'agentsDefaultAppAccessModalScreen',
  agentsDefaultGroupAccessModalScreen = 'agentsDefaultGroupAccessModalScreen',
  agentProfileScreen = 'agentProfileScreen',
  updateAccessDrawer = 'updateAccessDrawer',
  addGroupsToAgentModalScreen = 'addGroupsToAgentModalScreen',
}

export const agentsEvents: FeatureEvents<ScreenKeys, UIEventKeys, TrackEventKeys, string> = {
  confluenceSpec: 'https://hello.atlassian.net/wiki/spaces/CLOUDADMIN/pages/6814837006/Agents+M1+Analytics+Spec',
  screen: {
    agentsOverviewScreen: (): ScreenEvent => ({
      data: {
        name: AgentsScreens.agentsOverviewScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/100809',
    }),
    agentsAccessScreen: (): ScreenEvent => ({
      data: {
        name: AgentsScreens.agentsAccessScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/100810',
    }),
    agentsDefaultAppAccessModalScreen: (): ScreenEvent => ({
      data: {
        name: AgentsScreens.agentsDefaultAppAccessModalScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/100815',
    }),
    agentsDefaultGroupAccessModalScreen: (): ScreenEvent => ({
      data: {
        name: AgentsScreens.agentsDefaultGroupAccessModalScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/100816',
    }),
    agentProfileScreen: (attributes: { agentName: string; agentId: string; tab: 'apps' | 'groups' }): ScreenEvent => ({
      data: {
        name: AgentsScreens.agentProfileScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/100813',
    }),
    updateAccessDrawer: (attributes: { entityType: DirectoryEntityType }): ScreenEvent => ({
      data: {
        name: AgentsScreens.updateAccessDrawer,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/79566',
    }),
    addGroupsToAgentModalScreen: (attributes: { agentName: string; agentId: string }): ScreenEvent => ({
      data: {
        name: AgentsScreens.addGroupsToAgentModalScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/100817',
    }),
  },
  ui: {
    addGroupsToAllAgentsClicked: (attributes?: { groupsCount: number }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'addGroupsToAllAgentsClicked',
        source: AgentsScreens.agentsDefaultGroupAccessModalScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/100826',
    }),
    grantAccessClicked: (attributes?: { appsCount: number; workspaceIds: string[] }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'grantAccessClicked',
        source: AgentsScreens.agentsDefaultAppAccessModalScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/100828',
    }),
    addGroupsToAgentModalClicked: (attributes?: { groupsCount: number }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'addGroupsToAgentModalClicked',
        source: AgentsScreens.addGroupsToAgentModalScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/100830',
    }),
  },
  track: {
    addedAppToAgent: (attributes?: { appsCount: number; workspaceIds: string[] }): TrackEvent => ({
      data: {
        action: TrackAction.Added,
        actionSubject: TrackActionSubject.User,
        actionSubjectId: 'addedAppToAgent',
        source: AgentsScreens.agentsDefaultAppAccessModalScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/100821',
    }),
    removeAppToAgent: (attributes?: { appsCount: number }): TrackEvent => ({
      data: {
        action: TrackAction.Removed,
        actionSubject: TrackActionSubject.User,
        actionSubjectId: 'removeAppToAgent',
        source: 'agentAccessAppsTable',
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/100822',
    }),
    addedGroupToAgent: (attributes?: { groupsCount: number }): TrackEvent => ({
      data: {
        action: TrackAction.Added,
        actionSubject: TrackActionSubject.User,
        actionSubjectId: 'addedGroupToAgent',
        source: AgentsScreens.agentsDefaultGroupAccessModalScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/100831',
    }),
    removeGroupToAgent: (): TrackEvent => ({
      data: {
        action: TrackAction.Removed,
        actionSubject: TrackActionSubject.User,
        actionSubjectId: 'removeGroupToAgent',
        source: 'agentAccessGroupsTable',
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/100832',
    }),
  },
  operational: {},
};
